import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Grid, MenuItem, Slider, Stack, TextField, Typography } from "@mui/material";
import { crud, errorMessage, hd } from "../api";
import { useSettings } from "./GeneralSettings";
import { NumberField, SaveBar, Section, Toggle } from "./common";
import { T, TYPE } from "../../user/complaints/shared";

const PROVIDER_HELP = {
  platform: "Uses the JustTawk platform's Azure OpenAI deployment. No key needed.",
  azure: "Your own Azure OpenAI resource. The endpoint looks like https://<resource>.openai.azure.com/",
  openai: "Your own OpenAI account key (platform.openai.com). Leave the endpoint blank unless you use a compatible gateway.",
};

// Bring-your-own key. The key is write-only: the server stores it encrypted
// and only ever returns its last 4 characters.
function ProviderSection({ provider, onChange, dirty }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const p = provider || { mode: "platform" };
  const setP = (k) => (v) => onChange({ ...p, [k]: v });
  const own = p.mode !== "platform";
  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await hd.testAiProvider();
      setResult({ ok: true, text: `Connected — model ${r.model}, ${r.latencyMs} ms` });
    } catch (err) {
      setResult({ ok: false, text: errorMessage(err, "The AI provider did not respond") });
    } finally {
      setTesting(false);
    }
  };
  return (
    <Section title="AI provider & API key" description="Used by the helpdesk AI and the project management assistant.">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth size="small" label="Provider" value={p.mode} onChange={(e) => setP("mode")(e.target.value)} helperText={PROVIDER_HELP[p.mode]}>
            <MenuItem value="platform" disabled={!p.platformAvailable && p.mode !== "platform"}>JustTawk default{p.platformAvailable ? "" : " (not configured on server)"}</MenuItem>
            <MenuItem value="azure">Azure OpenAI (own key)</MenuItem>
            <MenuItem value="openai">OpenAI (own key)</MenuItem>
          </TextField>
        </Grid>
        {own && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={p.mode === "azure" ? "Deployment name" : "Model"} placeholder="gpt-4o-mini" value={p.model || ""} onChange={(e) => setP("model")(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={p.mode === "azure" ? 8 : 12}>
              <TextField fullWidth size="small" label={p.mode === "azure" ? "Endpoint" : "Endpoint (optional)"} placeholder={p.mode === "azure" ? "https://my-resource.openai.azure.com/" : "https://api.openai.com/v1/"} value={p.endpoint || ""} onChange={(e) => setP("endpoint")(e.target.value)} />
            </Grid>
            {p.mode === "azure" && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="API version" placeholder="2024-12-01-preview" value={p.apiVersion || ""} onChange={(e) => setP("apiVersion")(e.target.value)} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <TextField fullWidth size="small" type="password" autoComplete="new-password" label={p.hasKey ? `API key (saved, ends in ${p.keyLast4}) — type to replace` : "API key"}
                  value={p.apiKey || ""} onChange={(e) => setP("apiKey")(e.target.value)} />
                {p.hasKey && <Button size="small" color="error" onClick={() => onChange({ ...p, mode: "platform", apiKey: null })} sx={{ whiteSpace: "nowrap" }}>Remove key</Button>}
              </Stack>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button size="small" variant="outlined" onClick={test} disabled={testing || dirty}>{testing ? "Testing…" : "Test connection"}</Button>
            {dirty && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Save first, then test.</Typography>}
            {result && <Typography sx={{ fontSize: TYPE.small, color: result.ok ? "#067647" : "#B42318" }}>{result.text}</Typography>}
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
}

export default function AiSettings() {
  const { s, set, save, saving, dirty, reset } = useSettings();
  const [kbCats, setKbCats] = useState([]);
  useEffect(() => {
    crud("/kb/categories").list().then(setKbCats).catch(() => {});
  }, []);
  if (!s) return <CircularProgress size={22} />;
  const ai = s.ai;
  const setA = (k) => set("ai", k);
  const setNested = (k, sub) => (v) => setA(k)({ ...ai[k], [sub]: v });
  return (
    <Box>
      {!s.aiProviderConfigured && <Alert severity="warning" sx={{ mb: 2 }}>No AI provider is configured. Add your own Azure OpenAI or OpenAI key below, or set the JUSTTWAK_IN_AZURE_OPENAI_* environment variables on the server.</Alert>}
      <ProviderSection provider={ai.provider} onChange={setA("provider")} dirty={dirty} />
      <Section title="Features">
        <Toggle label="Enable AI for this workspace" checked={ai.enabled} onChange={setA("enabled")} />
        <Toggle label="AI chatbot answers customers in live chat" checked={ai.chatbotEnabled} onChange={setA("chatbotEnabled")} disabled={!ai.enabled} />
        <Toggle label="AI copilot for agents (summaries, suggested replies, translation)" checked={ai.copilotEnabled} onChange={setA("copilotEnabled")} disabled={!ai.enabled} />
        <Toggle label="Triage new tickets automatically (intent, sentiment, category, priority)" checked={ai.autoTriage} onChange={setA("autoTriage")} disabled={!ai.enabled} />
        <TextField select size="small" label="Triage results" value={ai.triageMode} onChange={(e) => setA("triageMode")(e.target.value)} sx={{ ml: 5.5, mb: 1, minWidth: 320 }} disabled={!ai.enabled}>
          <MenuItem value="suggest">Suggest only — agents apply</MenuItem>
          <MenuItem value="apply">Apply automatically when confident (empty fields only)</MenuItem>
        </TextField>
        <Toggle label="AI auto-replies to emails when a published article answers it" checked={ai.autoReplyEmail} onChange={setA("autoReplyEmail")} disabled={!ai.enabled} help="Only sent above the confidence threshold with a cited article; otherwise a draft is added as an internal note." />
        <Toggle label="Agents may return a chat to the AI" checked={ai.allowReturnToAi} onChange={setA("allowReturnToAi")} disabled={!ai.enabled} />
      </Section>
      <Section title="Model">
        <Grid container spacing={2}>
          {(ai.provider?.mode || "platform") === "platform" && <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Azure deployment (blank = server default)" value={ai.deployment} onChange={(e) => setA("deployment")(e.target.value)} /></Grid>}
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Assistant name" value={ai.botName} onChange={(e) => setA("botName")(e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: TYPE.small }}>Temperature: {ai.temperature}</Typography>
            <Slider size="small" min={0} max={1} step={0.1} value={ai.temperature} onChange={(_, v) => setA("temperature")(v)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: TYPE.small }}>Confidence threshold: {Math.round(ai.confidenceThreshold * 100)}%</Typography>
            <Slider size="small" min={0.3} max={0.95} step={0.05} value={ai.confidenceThreshold} onChange={(_, v) => setA("confidenceThreshold")(v)} />
          </Grid>
          <Grid item><NumberField label="Max response tokens" value={ai.maxResponseTokens} onChange={setA("maxResponseTokens")} min={100} max={2000} /></Grid>
          <Grid item><NumberField label="Unanswered replies before handoff" value={ai.maxFailedAnswers} onChange={setA("maxFailedAnswers")} min={1} max={10} /></Grid>
          <Grid item xs={12}>
            <Autocomplete multiple freeSolo options={["en", "hi", "hinglish", "ar"]} value={ai.languages} onChange={(_, v) => setA("languages")(v)} renderInput={(p) => <TextField {...p} size="small" label="Languages" helperText="The assistant replies in the customer's language" />} />
          </Grid>
        </Grid>
      </Section>
      <Section title="Knowledge sources" description="The assistant only answers from published knowledge base articles.">
        <Toggle label="Articles, guides and troubleshooting" checked={ai.knowledgeSources.articles} onChange={setNested("knowledgeSources", "articles")} />
        <Toggle label="FAQs" checked={ai.knowledgeSources.faqs} onChange={setNested("knowledgeSources", "faqs")} />
        <Autocomplete multiple options={kbCats} getOptionLabel={(c) => c.name} value={kbCats.filter((c) => (ai.knowledgeSources.categoryIds || []).map(String).includes(String(c._id)))}
          onChange={(_, v) => setNested("knowledgeSources", "categoryIds")(v.map((c) => c._id))}
          renderInput={(p) => <TextField {...p} size="small" label="Limit to categories (empty = all)" />} sx={{ mt: 1 }} />
        <Toggle label="Let the assistant see the customer's name and their own ticket status" checked={ai.customerContext} onChange={setA("customerContext")} help="Internal notes, other customers and internal CRM data are never shared with the AI assistant's customer answers." />
      </Section>
      <Section title="Hand off to a human when…">
        <Toggle label="The customer asks for a human (English, Hindi, Hinglish, Arabic)" checked={ai.handoff.onCustomerRequest} onChange={setNested("handoff", "onCustomerRequest")} />
        <Toggle label="Sentiment is negative" checked={ai.handoff.onNegativeSentiment} onChange={setNested("handoff", "onNegativeSentiment")} />
        <Toggle label="Confidence is below the threshold" checked={ai.handoff.onLowConfidence} onChange={setNested("handoff", "onLowConfidence")} />
        <Toggle label="Several questions went unanswered" checked={ai.handoff.onFailedAnswers} onChange={setNested("handoff", "onFailedAnswers")} />
        <Toggle label="The customer is marked VIP" checked={ai.handoff.onVip} onChange={setNested("handoff", "onVip")} />
        <NumberField label="Priority level at or above (0 = off)" value={ai.handoff.minPriorityLevel} onChange={setNested("handoff", "minPriorityLevel")} sx={{ mt: 1 }} />
        <Autocomplete multiple freeSolo options={["refund", "legal", "security", "complaint_escalation", "cancellation", "billing_dispute"]} value={ai.handoff.intents}
          onChange={(_, v) => setNested("handoff", "intents")(v)} renderTags={(v, gp) => v.map((o, i) => <Chip size="small" label={o} {...gp({ index: i })} key={o} />)}
          renderInput={(p) => <TextField {...p} size="small" label="Intents that always go to a human" />} sx={{ mt: 2 }} />
      </Section>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>API keys are stored encrypted on the server and are never sent back to the browser.</Typography>
      <SaveBar dirty={dirty} saving={saving} onSave={() => save(["ai"])} onReset={reset} />
    </Box>
  );
}
